import Dexie from 'dexie';
import * as moment from 'moment';
import isEqual from 'lodash-es/isEqual';
import { logSystem } from 'logger';

import { db } from '../database';
import { encrypt_item, decrypt_item } from '../support/crypt';

export const UserStateRecordSchema = '++, [key+userid], changed';

export class UserStateRecord {
	constructor(
		public key: string,
		public userid: string,
		public state: string,
		public version: number = 0,
		public changed?: string
	) {
		//
	}

	public async save(): Promise<void> {
		this.changed = moment().toISOString();
		this.version += 1;
		await db.userstate.put(this);
	}

	public age(): number {
		const { changed } = this;
		if (changed) {
			const now = moment();
			const old = moment(changed);
			return now.diff(old, 'hours');
		}
		return 0;
	}
}

/**
 * If the WebSocket isn't open, we queue refresh calls that are then processed
 * when the socket opens. One refresh is queued per key so that we don't do a
 * bunch of redundant refresh calls.
 *
 * A write-through overrides any pending refresh for that key.
 */
const pendingRefresh = new Map<string, () => void>();

const waitForWS = (): Promise<any> => {
	return new Promise<any>((resolve) => {
		(function waitForIt() {
			if (window.hcstatic.sitews) {
				resolve(window.hcstatic.sitews);
			} else {
				setTimeout(waitForIt, 50);
			}
		})();
	});
};

async function processPendingRefresh(): Promise<void> {
	const sitews = await waitForWS();
	sitews.on('open', () => {
		pendingRefresh.forEach((refresh, key) => {
			refresh();
			pendingRefresh.delete(key);
		});
	});
}

processPendingRefresh();

/**
 * Special handling for session id.
 */
export async function check_session_id(sessionid: number): Promise<void> {
	const { portal_userid: userid = '' } = window.hcstatic;
	const key = 'session-id';
	let record = await db.userstate.where({ key, userid }).first();
	const sessid = record !== undefined ? +record.state : undefined;
	logSystem.info(() => `check_session_id(${sessionid}) for userid="${userid}"; old session-id=${sessid}`);
	if (sessid !== sessionid) {
		await purge_user_state();
		record = new UserStateRecord(key, userid, sessionid.toString());
		record.save();
	}
}

/**
 * Purge all state for the current user.
 */
export async function purge_user_state(): Promise<number> {
	const { portal_userid: userid = '' } = window.hcstatic;
	const count = await db.userstate
		.where('[key+userid]')
		.between([Dexie.minKey, userid], [Dexie.maxKey, userid])
		.delete();
	logSystem.info(() => `purge_user_state for ${userid}; purged ${count} records`);
	return count;
}

/**
 * Implements a write-through cache. If the WebSocket isn't open, the
 * write-through is delayed until it opens.
 */
export async function set_user_state<T = any, U = string>({
	key,
	state,
	setstate
}: {
	key: U;
	state: T;
	setstate: (params: { key: U; state: T }) => Promise<any>;
}): Promise<void> {
	const keyval = key as any as string;
	const { portal_userid: userid = '' } = window.hcstatic;
	const refreshKey = `${keyval}+${userid}`;
	// remove any pending refresh
	if (pendingRefresh.has(refreshKey)) {
		pendingRefresh.delete(refreshKey);
	}
	let record = await db.userstate.where({ key: keyval, userid }).first();
	const stateString = await encrypt_item({ _v_: state });
	if (record === undefined) {
		record = new UserStateRecord(keyval, userid, stateString);
		record.save();
	} else {
		record.state = stateString;
		record.save();
		/*
		db.userstate.where({ key: keyval, userid }).modify({
			changed: moment().toISOString(),
			state: stateString,
			version: record.version + 1
		});
		*/
	}
	const { sitews } = window.hcstatic;
	if (sitews.is_open()) {
		await setstate({ key, state });
	} else {
		pendingRefresh.set(refreshKey, () => {
			setstate({ key, state });
		});
	}
}

/**
 * Get user state using delayed read (cache-first) where possible.
 */
export async function get_user_state<T = any, U = string>({
	key,
	getstate,
	statechange
}: {
	key: U;
	getstate: (params: { key: U }) => Promise<T>;
	statechange?: (state: T) => void;
}): Promise<T> {
	const keyval = key as any as string;
	const { portal_userid: userid = '' } = window.hcstatic;
	let record = await db.userstate.where({ key: keyval, userid }).first();
	if (record === undefined) {
		const state = await getstate({ key });
		record = new UserStateRecord(keyval, userid, await encrypt_item({ _v_: state }));
		record.save();
	} else {
		const { sitews } = window.hcstatic;
		const refresh = async () => {
			const state = await getstate({ key });
			const recstate = await decrypt_item<T>(record!.state);
			if (!isEqual(state, recstate._v_)) {
				db.userstate.where({ key: keyval, userid }).modify({
					changed: moment().toISOString(),
					state: await encrypt_item({ _v_: state }),
					version: record!.version + 1
				});
				statechange && statechange(state);
			}
		};
		if (sitews.is_open()) {
			refresh();
		} else {
			const refreshKey = `${keyval}+${userid}`;
			pendingRefresh.set(refreshKey, refresh);
		}
	}
	const state: { [key: string]: T } = await decrypt_item<T>(record.state);
	return state._v_;
}

/**
 * Determine if a key currently has a cached value.
 */
export async function has_user_state(key: string): Promise<boolean> {
	const { portal_userid: userid = '' } = window.hcstatic;
	const record = await db.userstate.where({ key, userid }).first();
	return record !== undefined;
}
